import React, { useState, useContext } from 'react';
import CountryFlag from '../../utils/CountryFlag';
import { useMetadata } from '../../context/MetadataContext';
import { ThemeContext } from '../../context/ThemeContext';
import styles from './UserProfile.module.css';
import { QrCode } from 'lucide-react';
import CreatedPuzzles from '../CreatedPuzzles';
import QRCodeModal from '../QRCodeModal';
import { useUserProfile } from '../../hooks/useUserProfile';

const UserProfile = ({ onClose, onOpenNicknameModal }) => {
  const [showCreatedPuzzles, setShowCreatedPuzzles] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const { metadata, getText } = useMetadata();
  const { isDarkMode } = useContext(ThemeContext);
  const {
    profile,
    loading,
    error,
    changeLanguage,
    changeTheme,
    changeMode,
    changeBirthYear,
    changeGender,
  } = useUserProfile();

  // Helper function to get theme name from code
  const getThemeName = (themeCode) => {
    if (!metadata || !metadata.themes) return themeCode;
    
    const theme = metadata.themes.find(t => t.code === themeCode);
    return theme ? theme.name : themeCode;
  };

  const handleLanguageChange = (e) => changeLanguage(e.target.value);
  
  const handleThemeChange = (e) => changeTheme(e.target.value);
  
  // Handle UI theme mode change
  const handleModeChange = () => changeMode();

  const handleBirthYearChange = (e) =>
    changeBirthYear(e.target.value === '' ? null : parseInt(e.target.value));

  const handleGenderChange = (e) =>
    changeGender(e.target.value === '' ? null : e.target.value);

  // Generate birth year options (current year - 100 to current year - 5)
  const generateBirthYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 5; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  };

    return (
      <div className={`${styles.profileContainer} ${styles.open}`}>
      <div className={styles.profileHeader}>
        <h2 className={styles.profileTitle}>{getText('user_profile.title')}</h2>
        <button className={styles.closeButton} onClick={onClose}>×</button>
      </div>
      
      {loading ? (
        <div className={`${styles.profileContent} ${styles.loading}`}>{getText('common_text.loading')}...</div>
      ) : error ? (
        <div className={`${styles.profileContent} ${styles.error}`}>{error}</div>
      ) : profile && (
        <div className={styles.profileContent}>
          <div className={styles.profileSection}>
            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('common_text.nickname')}</span>
              <span className={styles.profileValue}>
                <button 
                  className={styles.nicknameLink}
                  onClick={onOpenNicknameModal}
                >
                  {profile.nickname}
                </button>
              </span>
            </div>
            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('common_text.country')}</span>
              <span className={styles.profileValue}>
                <CountryFlag countryCode={profile.country_code} />
              </span>
            </div>

            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('common_text.birth_year')}</span>
              <select 
                className={styles.birthYearSelector}
                value={profile.birth_year || ''}
                onChange={handleBirthYearChange}
              >
                <option value="">--Not specified--</option>
                {generateBirthYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('common_text.gender')}</span>
              {!metadata || !metadata.genders ? (
                <span className={styles.profileValue}>{profile.gender || '--Not specified--'}</span>
              ) : (
                <select 
                  className={styles.genderSelector}
                  value={profile.gender || ''}
                  onChange={handleGenderChange}
                >
                  <option value="">--Not specified--</option>
                  {metadata.genders.map(gender => (
                    <option key={gender.code} value={gender.code}>
                      {gender.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('user_profile.playing_since')}</span>
              <span className={styles.profileValue}>
                {new Date(profile.created_ts * 1000).toLocaleDateString()}
              </span>
            </div>

          </div>
          
          <div className={styles.profileSection}>
            {profile.preferences && (
              <>
                <div className={styles.profileInfoRow}>
                  <span className={styles.profileLabel}>{getText('common_text.language')}</span>
                  {!metadata || !metadata.languages ? (
                    <span className={styles.profileValue}>{profile.preferences.language_code}</span>
                  ) : (
                    <select 
                      className={styles.languageSelector}
                      value={profile.preferences.language_code}
                      onChange={handleLanguageChange}
                    >
                      {metadata.languages.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className={styles.profileInfoRow}>
                  <span className={styles.profileLabel}>{getText('common_text.theme')}</span>
                  {!metadata || !metadata.themes ? (
                    <span className={styles.profileValue}>{getThemeName(profile.preferences.color_palette)}</span>
                  ) : (
                    <select 
                      className={styles.themeSelector}
                      value={profile.preferences.color_palette}
                      onChange={handleThemeChange}
                    >
                      {metadata.themes.map(theme => (
                        <option key={theme.code} value={theme.code}>
                          {theme.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className={styles.profileInfoRow}>
                <span className={styles.profileLabel}>{getText('common_text.light_mode')}:</span>
                <div className={styles.modeToggleWrapper}>
                    <label className={styles.modeToggle}>
                    <input 
                        type="checkbox" 
                        checked={isDarkMode}
                        onChange={handleModeChange}
                    />
                    <span className={styles.modeToggleSlider}></span>
                    </label>
                    <span className={styles.modeToggleLabel}>
                    {isDarkMode ? getText('common_text.dark') : getText('common_text.light')}
                    </span>
                </div>
                </div>
              </>
            )}
          </div>
          
         
          <div className={styles.profileSection}>

            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('user_profile.user_key')}</span>
              <span className={styles.profileValue}>
                {profile.user_key}
              </span>
            </div>
            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('user_profile.linking_code')}</span>
              <span className={styles.profileValue}>
                {profile.linking_code}
              </span>
            </div>
            <div className={styles.profileInfoRow}>
              <span className={styles.profileLabel}>{getText('user_profile.link_via_qr')}</span>
              <button
                className={styles.qrButton}
                onClick={() => setShowQRModal(true)}
              >
                <QrCode size={20} />
              </button>
            </div>
            

         
          {/* <div className={styles.actionSection}>
            <h3>{getText('actions.actions')}</h3>
            <div className={styles.actionRow}>
              <span className={styles.profileLabel}>{getText('user_profile.unlink_device')}</span>

            </div>
            
            <div className={styles.actionRow}>
              <button 
                className={styles.actionButton}
                onClick={() => setShowCreatedPuzzles(true)}
              >
                {getText('user_profile.my_created_puzzles')}
              </button>
            </div>
            
          </div>

</div> */}
        </div>
          
        </div>

      )}
      
      <CreatedPuzzles
        isOpen={showCreatedPuzzles}
        onClose={() => setShowCreatedPuzzles(false)}
      />

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        userKey={profile?.user_key}
        linkingCode={profile?.linking_code}
      />
    </div>
  );
};

export default UserProfile;
